import { Textarea } from "@mantine/core";
import { useCallback, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import CustomSelect from "src/generator/_Selects/CustomSelect/CustomSelect";
import DatePicker from "src/generator/DatePicker/DatePicker";
import { useLogbookRefsContext } from "src/generator/Logbook/LogbookRefsContext";
import { vesselTypeData } from "src/mock/selectOptions";
import { type IYachtData } from "src/pages/Reports/Report/Report.types";
import { useSelector } from "src/redux/hooks";
import { validateDate } from "src/utils";

interface Props {
  id: string;
  unit: string;
  value: string;
  required: boolean;
}

const YachtDataPopulate = ({ id, unit, value, required }: Props) => {
  const refProps = useLogbookRefsContext(id);
  const yachtdata = useSelector(state => state.generatorSlice.yachtData);

  const isDate = useMemo(
    () => unit === "biofoul.keel" || unit === "biofoul.deldate",
    [unit],
  );

  const getSettedData = useCallback(() => {
    let result: any = null;
    const [firstDeep, ...unitParts] = unit.split(".");
    if (yachtdata) {
      result = yachtdata[firstDeep as keyof IYachtData];
      if (result) {
        for (const part of unitParts) {
          result = result[part];
          if (!result) break;
        }
      }
    }
    return isDate ? validateDate(result) : result;
  }, [isDate, unit, yachtdata]);

  const uppercase = useSelector(state => state.preferencesSlice.uppercase);
  const { control } = useFormContext();
  const defaultValue = useMemo(
    () => (value && value.length > 0 ? value : getSettedData()),
    [value, getSettedData],
  );
  const { field } = useController({
    control,
    name: id,
    defaultValue,
  });

  if (unit === "vtype") {
    return (
      <CustomSelect
        data={vesselTypeData}
        id={id}
        value={value}
        required={required}
        readOnly={
          !!defaultValue &&
          defaultValue.length > 0 &&
          defaultValue === getSettedData()
        }
      />
    );
  }

  if (isDate) {
    return <DatePicker id={id} externalValue={value} required={required} />;
  }

  return (
    <Textarea
      autosize
      minRows={1}
      miw={200}
      readOnly={
        !!defaultValue &&
        defaultValue.length > 0 &&
        defaultValue === getSettedData()
      }
      styles={{
        input: {
          paddingTop: "6px !important",
          paddingBottom: "6px !important",
          textTransform: uppercase ? "uppercase" : "none",
        },
      }}
      {...field}
      {...refProps}
    />
  );
};

export default YachtDataPopulate;
